import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { createPaymentIntent, confirmPayment, savePaymentMethod } from '../../services/paymentService';

import { COUNTRIES } from '../../constants/countries';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const StripePaymentForm = ({ plan, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isRecurring, setIsRecurring] = useState(false);
  const [cardholderName, setCardholderName] = useState('');
  const [country, setCountry] = useState(''); // Use ISO code as default
  const hasCreatedIntentRef = React.useRef(false);
  const planIdRef = React.useRef(null);
  const requestInProgressRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  const hasCalledSuccessRef = React.useRef(false);

  // Handle global Stripe errors
  useEffect(() => {
    const handleStripeError = (event) => {
      if (event.detail && event.detail.error) {
        console.warn('Stripe error caught:', event.detail.error);
        // Don't show these internal Stripe errors to users
      }
    };

    window.addEventListener('stripe-error', handleStripeError);

    return () => {
      window.removeEventListener('stripe-error', handleStripeError);
    };
  }, []);
  const stripe = useStripe();
  const elements = useElements();
  const { user, token } = useSelector((state) => state.auth);

  // Initialize mounted ref on mount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset when plan or discount code changes
  useEffect(() => {
    const currentPlanId = plan?.stripePriceId || plan?.id || plan?.name;
    const currentDiscountCode = plan?.discountCode || '';
    const identifyingState = `${currentPlanId}_${currentDiscountCode}`;

    if (planIdRef.current !== null && planIdRef.current !== identifyingState) {
      // Plan or discount changed, reset state
      hasCreatedIntentRef.current = false;
      requestInProgressRef.current = false;
      hasCalledSuccessRef.current = false;
      setClientSecret('');
      setError(null);
      setIsCreatingIntent(false);
      setSubscriptionId(null);
      setIsRecurring(false);
    }
    planIdRef.current = identifyingState;
  }, [plan?.stripePriceId, plan?.id, plan?.name, plan?.discountCode]);
  // Use specific properties to avoid object reference changes

  // Create payment intent when component mounts - ONLY ONCE per plan
  useEffect(() => {
    // Prevent multiple calls - check refs FIRST (before any async operations)
    // Don't check clientSecret here - we want to create it if it doesn't exist
    if (
      hasCreatedIntentRef.current ||
      requestInProgressRef.current ||
      isCreatingIntent ||
      !plan ||
      !token ||
      !user?.email
    ) {
      if (!plan || !token || !user?.email) {
        // Don't show error if just waiting for data
        if (plan && token && !user?.email) {
          setError('User email is required for payment. Please update your profile.');
          if (onError) {
            onError(new Error('User email is required for payment. Please update your profile.'));
          }
        }
      }
      return;
    }

    // If we already have clientSecret, don't create again
    if (clientSecret) {
      return;
    }

    const createIntent = async () => {
      // Mark request as in progress IMMEDIATELY (before async call)
      requestInProgressRef.current = true;
      setIsCreatingIntent(true);

      try {
        // Pass the full plan object and user email
        const response = await createPaymentIntent(plan, token, user?.email);

        if (response && response.clientSecret) {
          // Always set state - React handles unmounted components gracefully
          setClientSecret(response.clientSecret);
          setIsRecurring(response.isRecurring || false);
          setSubscriptionId(response.subscriptionId || null);
          hasCreatedIntentRef.current = true;
          requestInProgressRef.current = false;
          setIsCreatingIntent(false);
        } else {
          console.error('Invalid response structure:', response);
          requestInProgressRef.current = false;
          setIsCreatingIntent(false);
          throw new Error('Invalid response from payment server. Please try again.');
        }
      } catch (err) {
        console.error('Payment intent creation error:', err);
        requestInProgressRef.current = false;
        setIsCreatingIntent(false);

        // Always set error state - React handles unmounted components
        const errorMessage = err.message || 'Failed to initialize payment. Please try again.';
        setError(errorMessage);
        if (onError) {
          onError(err);
        }
      }
    };

    createIntent();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.stripePriceId, plan?.id, plan?.name, plan?.discountCode, token, user?.email]); // Use specific plan properties

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    // Prevent duplicate submissions
    if (hasCalledSuccessRef.current) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        setError('Card information is required');
        setLoading(false);
        return;
      }

      // Check if it's a setup intent or payment intent
      const isSetupIntent = clientSecret.startsWith('seti_');

      let result;

      if (isSetupIntent) {
        // For Setup Intent (subscriptions), use confirmCardSetup
        let setupResult;
        try {
          setupResult = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
              card: cardElement,
            }
          });
        } catch (stripeError) {
          console.error('Stripe setup error:', stripeError);
          setError(stripeError.message || 'Failed to confirm card setup. Please try again.');
          setLoading(false);
          return;
        }

        const { error: setupError, setupIntent } = setupResult;

        if (setupError) {
          console.error('Stripe setup error:', setupError);
          setError(setupError.message);
          setLoading(false);
          return;
        }

        if (setupIntent && setupIntent.status === 'succeeded') {
          try {
            // Use the subscriptionId from the response
            if (subscriptionId) {
              result = await confirmPayment(
                null,
                token,
                subscriptionId,
                plan?.discountCode || null
              );

              // Check payment status from response
              const paymentStatus = result?.paymentStatus || 'success';

              if (paymentStatus === 'failed') {
                setError(result?.message || 'Payment not completed. Please try again.');
                setLoading(false);
                if (onError) {
                  onError(new Error(result?.message || 'Payment failed'));
                }
                return;
              }

              if (paymentStatus === 'processing') {
                setError(result?.message || 'Payment is being processed. Please wait...');
                setLoading(false);
                // Don't call onError for processing status - it's not a failure
                return;
              }

              // Only proceed if paymentStatus is 'success'
              if (paymentStatus === 'success' && result) {
                // Normalize the response - ensure tokens are calculated correctly
                // If tokens is 0 but tokensAdded exists, calculate the new total
                if (result.tokens === 0 && result.tokensAdded && user) {
                  result.tokens = (user.tokens || 0) + result.tokensAdded;
                }

                // Ensure subscription data is properly structured
                if (result.subscription) {
                  result.subscription = {
                    ...result.subscription,
                    subscriptionId: result.subscriptionId || result.subscription.subscriptionId,
                    plan: result.plan || result.subscription.plan,
                    status: result.subscription.status || 'active',
                    provider: result.subscription.provider || 'stripe',
                    tokens: result.tokensAdded || result.subscription.tokens || result.tokens
                  };
                }

                // Ensure isRecurring flag is set
                result.isRecurring = result.isRecurring !== undefined ? result.isRecurring : true;

                // Prevent duplicate success calls
                if (!hasCalledSuccessRef.current) {
                  hasCalledSuccessRef.current = true;

                  // Clear the form
                  cardElement.clear();

                  // Call success callback with normalized result
                  onSuccess(result);
                }
              } else {
                setError('Payment status is unknown. Please contact support.');
                setLoading(false);
                return;
              }
            } else {
              throw new Error('Subscription ID is missing');
            }
          } catch (backendError) {
            console.error('Backend subscription confirmation error:', backendError);
            setError('Card setup succeeded but failed to activate subscription. Please contact support.');
            setLoading(false);
            return;
          }
        } else {
          setError('Card setup was not successful. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        // For Payment Intent (one-time payments), use confirmCardPayment
        let paymentResult;
        try {
          paymentResult = await stripe.confirmCardPayment(clientSecret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
                address: {
                  country: country,
                }
              }
            }
          });
        } catch (stripeError) {
          console.warn('Stripe internal error (usually harmless):', stripeError);
          // Try to continue with the payment flow
          paymentResult = { error: null, paymentIntent: null };
        }

        const { error: paymentError, paymentIntent } = paymentResult;

        if (paymentError) {
          console.error('Stripe payment error:', paymentError);
          setError(paymentError.message);
          setLoading(false);
          return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          try {
            // Confirm payment with your backend
            result = await confirmPayment(
              paymentIntent.id,
              token,
              null,
              plan?.discountCode || null
            );

            // Check payment status from response
            const paymentStatus = result?.paymentStatus || 'success';

            if (paymentStatus === 'failed') {
              setError(result?.message || 'Payment not completed. Please try again.');
              setLoading(false);
              if (onError) {
                onError(new Error(result?.message || 'Payment failed'));
              }
              return;
            }

            if (paymentStatus === 'processing') {
              setError(result?.message || 'Payment is being processed. Please wait...');
              setLoading(false);
              // Don't call onError for processing status - it's not a failure
              return;
            }

            // Only proceed if paymentStatus is 'success'
            if (paymentStatus === 'success' && result) {
              // Normalize the response - ensure tokens are calculated correctly
              // If tokens is 0 but tokensAdded exists, calculate the new total
              if (result.tokens === 0 && result.tokensAdded && user) {
                result.tokens = (user.tokens || 0) + result.tokensAdded;
              }

              // Save payment method if user opted to save card (only for payment intents)
              if (saveCard && paymentIntent.payment_method) {
                try {
                  await savePaymentMethod(paymentIntent.payment_method, token);
                } catch (saveError) {
                  console.warn('Failed to save payment method:', saveError);
                  // Don't fail the payment if saving the card fails
                }
              }

              // Prevent duplicate success calls
              if (!hasCalledSuccessRef.current) {
                hasCalledSuccessRef.current = true;

                // Clear the form
                cardElement.clear();

                // Call success callback with normalized result
                onSuccess(result);
              }
            } else {
              setError('Payment status is unknown. Please contact support.');
              setLoading(false);
              return;
            }
          } catch (backendError) {
            console.error('Backend payment confirmation error:', backendError);
            setError('Payment succeeded but failed to update account. Please contact support.');
            setLoading(false);
            return;
          }
        } else {
          setError('Payment was not successful. Please try again.');
          setLoading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* Card Information */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Card information
          </label>
          <div className="p-4 border border-gray-200 rounded-xl bg-white shadow-sm">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          {error && (
            <p className="text-red-600 text-sm mt-2">{error}</p>
          )}
        </div>

        {/* Cardholder Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Cardholder name
          </label>
          <input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="Micah Alexander Jnr"
            className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
            required
          />
        </div>

        {/* Country/Region */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Country or region
          </label>
          <div className="relative">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none appearance-none transition-all"
            >
              <option value="">Select Country</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-2 py-4 border-t border-gray-100">
        <input
          type="checkbox"
          id="saveCard"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
          className="h-5 w-5 text-[#1a6b54] focus:ring-[#1a6b54] border-gray-300 rounded"
        />
        <label htmlFor="saveCard" className="text-sm text-gray-600">
          Save this card for future purchases
        </label>
      </div>

      <button
        type="submit"
        disabled={!stripe || loading || !clientSecret}
        className="w-full bg-[#1a6b54] hover:bg-[#145341] text-white py-4 text-lg font-bold rounded-xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="flex justify-center items-center gap-2">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Processing...</span>
          </div>
        ) : !clientSecret ? (
          'Loading...'
        ) : (
          'Purchase'
        )}
      </button>

      <p className="text-xs text-gray-400 text-center leading-relaxed max-w-xs mx-auto">
        By confirming your purchase, you allow NKENNEAi to charge you for
        this payment in accordance with their terms.
      </p>
    </form>
  );
};

export default StripePaymentForm;
